import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getItemWithExpiry } from '@wavvest/shared-utils';
import { store } from '@/store';
import { setUser } from '@/store/features/userinfo/userinfoSlice';
import { AdvisorLoginRouteLoader } from '@/pages/login/route-loader';
import { shouldSuppressAuthRouteLoader } from '@/routes/auth-loader-state';

interface PublicRouteProps {
  children: React.ReactNode;
  redirectAuthenticated?: boolean;
}

const PublicRoute: React.FC<PublicRouteProps> = ({ children, redirectAuthenticated = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [shouldShowChildren, setShouldShowChildren] = useState(false);
  const suppressAuthLoader = shouldSuppressAuthRouteLoader(location.state);

  useEffect(() => {
    const checkAuthStatus = () => {
      try {
        const token = getItemWithExpiry('access_token');
        const userStr = getItemWithExpiry('user');

        if (token && userStr && redirectAuthenticated) {
          // 用户已登录且 token 未过期，重定向到 home 页面
          try {
            const user = JSON.parse(userStr);
            // 恢复用户状态到 Redux store
            store.dispatch(setUser(user));
            // 跳转到 home 页面
            navigate('/home', { replace: true });
          } catch (error) {
            console.error('Failed to parse user data:', error);
            // 如果用户数据解析失败，清除存储并允许访问公共页面
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            setShouldShowChildren(true);
          }
        } else {
          // 用户未登录或 token 已过期，允许访问公共页面
          if (token && userStr) {
            try {
              store.dispatch(setUser(JSON.parse(userStr)));
            } catch (error) {
              console.error('Failed to parse user data:', error);
              localStorage.removeItem('access_token');
              localStorage.removeItem('user');
            }
          }
          setShouldShowChildren(true);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setShouldShowChildren(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuthStatus();
  }, [navigate, redirectAuthenticated]);

  // 显示加载状态
  if (isLoading) {
    if (suppressAuthLoader) {
      return null;
    }

    return <AdvisorLoginRouteLoader />;
  }

  // 如果用户已登录，这里通常不会被渲染，因为会被 navigate 重定向
  // 如果用户未登录，显示子组件（登录页面等）
  if (shouldShowChildren) {
    return <>{children}</>;
  }

  return null;
};

export default PublicRoute;
